import Image from 'next/image';
import Icons from 'components/atoms/Icons';
import { useFilter } from 'components/context/FilterContext';
import React, { useEffect, useState } from 'react';
import constant from 'assets/utils/constant';

type LocationSidebarProps = {
	img: {
		value: {
			alt: string;
			height: string;
			src: string;
			thumbnailsrc: string;
			width: string;
		};
	};
	locationName: string;
	street1: string;
	suite: string;
	city: string;
	state: string;
	zip: string;
	phone: string;
};

const LocationWidget = (props: LocationSidebarProps): JSX.Element => {
	const { img, locationName, street1, suite, city, state, zip, phone } = props;
	const [mapUrl, setMapUrl] = useState('');
	const { filterState } = useFilter();
	const userLocationContext = filterState.userLocation;
	const googleApiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

	const buildStaticMapUrl = (street1: string, city: string, state: string, zip: string) => {
		const markerUrl = encodeURIComponent(
			`icon:${window?.location?.origin}${constant.api.googleMapsMarkerUrl}`
		);

		const mapCenter = `${street1},${city},${state}+${zip}`;

		const parameters = {
			center: mapCenter,
			zoom: '15',
			size: '412x233',
			scale: '2',
			style: 'feature:poi|visibility:off',
			markers: `${markerUrl}|scale:2|${mapCenter}`,
			key: googleApiKey,
		};

		return `https://maps.googleapis.com/maps/api/staticmap?center=${parameters.center}&zoom=${parameters.zoom}&size=${parameters.size}&scale=${parameters.scale}&style=${parameters.style}&markers=${parameters.markers}&key=${parameters.key}`;
	};

	useEffect(() => {
		window && setMapUrl(buildStaticMapUrl(street1, city, state, zip).replaceAll(' ', '%20'));
	});

	return (
		<div className="location-widget">
			{/* update check here - not sure if we want something else*/}
			{mapUrl && (
				<div className="location-map" itemScope itemType="http://schema.org/Hospital">
					<a
						className="track-link-click"
						itemProp="hasMap"
						href={`https://www.google.com/maps/dir/${
							userLocationContext?.latitude ? userLocationContext?.latitude : ''
						}${userLocationContext?.latitude ? ',' : ''}${
							userLocationContext?.longitude ? userLocationContext?.longitude : ''
						}/${street1},${city},${state}+${zip}`.replaceAll(' ', '+')}
						target="_blank"
						rel="noopener noreferrer"
					>
						<Image height="466px" src={mapUrl} width="824px" alt={img?.value?.alt} />
					</a>
				</div>
			)}
			<p className="location-name osf-h4">{locationName}</p>
			<ul className="details">
				{street1 && (
					<li className="address">
						<Icons id="location" />
						<a
							className="address-link osf-intro-copy"
							itemProp="address"
							itemScope
							itemType="http://schema.org/PostalAddress"
							href={`https://maps.apple.com/?daddr=${street1},${suite},${city},${state} ${zip}`}
						>
							<span className="location-street">{street1}, </span>
							{suite && <span className="location-suite">{suite}</span>}
							<span className="location-city">{city} </span>
							<span className="location-state">{state} </span>
							<span className="location-zip">{zip}</span>
						</a>
					</li>
				)}
				{phone && (
					<li className="phone">
						<Icons id="phone" />
						<a
							className="phone-link osf-intro-copy"
							itemScope
							itemType="http://schema.org/Hospital"
							href={`tel:${phone}`}
							itemProp="telephone"
						>
							Call: {phone}
						</a>
					</li>
				)}
			</ul>
			<a
				href={`https://www.google.com/maps/dir/${
					userLocationContext?.latitude ? userLocationContext?.latitude : ''
				}${userLocationContext?.latitude ? ',' : ''}${
					userLocationContext?.longitude ? userLocationContext?.longitude : ''
				}/${street1},${city},${state}+${zip}`.replaceAll(' ', '+')}
				className="btn-primary"
				target="_blank"
				rel="noopener noreferrer"
			>
				<span className="osf-overview">Directions</span>
			</a>
		</div>
	);
};

export default LocationWidget;
